import { FC } from 'react';

import { findRootSvgElement } from '@adflow/ad';
import type { AdElement, Animation } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { Box } from '@chakra-ui/react';

import { useElementSize } from '../../hooks/useElementSize';
import { EditorCanvasControls } from './EditorCanvasControls';
import { EditorCanvasStage } from './EditorCanvasStage';
import { useEditorCanvasSession } from './useEditorCanvasSession';

/** Deterministic size for unit tests and lab fixtures. */
export const EDITOR_CANVAS_TEST_STAGE_SIZE = { width: 520, height: 400 } as const;

export type EditorCanvasProps = {
  elements: AdElement[];
  resolvedElements: AdElement[];
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  animation?: Animation | null;
  selectedElementIds: string[];
  onSelectionChange: (elementIds: string[]) => void;
  stageSize?: { width: number; height: number };
  fillContainer?: boolean;
  clipToFrame?: boolean;
  showControls?: boolean;
  stageTestId?: string;
};

export const EditorCanvas: FC<EditorCanvasProps> = ({
  elements,
  resolvedElements,
  animationOverrides = null,
  animation = null,
  selectedElementIds,
  onSelectionChange,
  stageSize: stageSizeProp,
  fillContainer = false,
  clipToFrame = false,
  showControls = true,
  stageTestId,
}) => {
  const { ref: stageMeasureRef, size: measuredStageSize } = useElementSize<HTMLDivElement>();
  const measuredReady = measuredStageSize.width > 0 && measuredStageSize.height > 0;
  const stageSize =
    stageSizeProp ??
    (fillContainer
      ? measuredReady
        ? measuredStageSize
        : { width: 0, height: 0 }
      : EDITOR_CANVAS_TEST_STAGE_SIZE);

  const root = findRootSvgElement(resolvedElements);
  const frame = {
    width: root?.size.width ?? 300,
    height: root?.size.height ?? 250,
  };

  const session = useEditorCanvasSession({
    elements,
    resolvedElements,
    animation,
    frame,
    stageSize,
    selectedElementIds,
    onSelectionChange,
    clipToFrame,
  });

  return (
    <Box
      position="relative"
      flex={fillContainer ? 1 : undefined}
      minH={fillContainer ? 0 : undefined}
      width={fillContainer ? '100%' : undefined}
      height={fillContainer ? '100%' : undefined}
      data-test-id="editor-canvas"
    >
      {fillContainer ? (
        <Box ref={stageMeasureRef} position="absolute" inset={0} display="flex">
          {measuredReady ? (
            <EditorCanvasStage
              session={session}
              frame={frame}
              stageSize={stageSize}
              animationOverrides={animationOverrides}
              testId={stageTestId}
              flex={1}
            />
          ) : null}
        </Box>
      ) : (
        <EditorCanvasStage
          session={session}
          frame={frame}
          stageSize={stageSize}
          animationOverrides={animationOverrides}
          testId={stageTestId}
        />
      )}
      {showControls ? <EditorCanvasControls session={session} /> : null}
    </Box>
  );
};

EditorCanvas.displayName = 'EditorCanvas';

export { useEditorCanvasSession } from './useEditorCanvasSession';
export type { EditorCanvasSession, UseEditorCanvasSessionArgs } from './useEditorCanvasSession';
export { EditorCanvasStage } from './EditorCanvasStage';
export { EditorCanvasControls } from './EditorCanvasControls';
